import { Form } from "@unform/web";
import { useEffect, useRef, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";
import { messageStatusLabel } from "Helpers/MessageStatus";
import { showNotification } from "Helpers/Toast";

import UseAuth from "Hooks/UseAuth";
import UseFile from "Hooks/UseFile";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseServices from "Hooks/UseServices";

import Button from "Components/Global/Buttons";
import Input from "Components/Global/Form/Input";
import GetIcon from "Components/Global/Icons";

import * as S from "./styles";

import { useTheme } from "styled-components";

const ConversationMessages = ({ projectId, conversation, setConversation }) => {
  const theme = useTheme();

  const { parsedJWT } = UseAuth();
  const { api } = UseServices();
  const { loading, setLoading } = UseGlobalLoading();
  const { uploadFile } = UseFile();

  const inputRef = useRef(null);

  const conversationRef = useRef(null);

  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  const [messages, setMessages] = useState([]);

  useEffect(() => {
    getConversationMessages(projectId);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (messages.length && conversationRef.current)
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
  }, [messages]);

  const getConversationMessages = (project_id) => {
    api
      .post("/projects/messages", {
        project_id: project_id,
        conversation_id: conversation.id,
      })
      .then((response) => {
        const messages = response.data?.messages;

        setMessages(messages);
      })
      .catch((error) => {
        setLoading(false);
        return HTTPErrorCallback(error);
      });
  };

  // Avaliação do arquivo pelo patrocinador (workflow D23, 29/08/2026).
  const evaluateMessage = async (message_id, approved) => {
    try {
      await api.post("/projects/messages/evaluate", { message_id, approved });

      getConversationMessages(projectId);
    } catch (error) {
      setLoading(false);
      return HTTPErrorCallback(error);
    }
  };

  const sendMessage = async () => {
    if (!text) return;

    setSending(true);

    try {
      let file_src = null;

      if (file) {
        const location = await uploadFile(file);
        file_src = location;
      }

      await api.post("/projects/messages/create", {
        text: text,
        file_name: file?.name,
        file_src,
        project_id: projectId,
        conversation_id: conversation.id,
      });

      getConversationMessages(projectId);

      setText("");
      setFile(null);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (file.size > 30000000) {
      return showNotification(
        "danger",
        "Atenção",
        "O arquivo deve conter no máximo 30 MB.",
      );
    }

    setFile(file);
  };

  return (
    <S.Wrapper>
      <Form>
        <S.Buttons>
          <Button
            type="button"
            text="Voltar"
            onClick={() => setConversation(null)}
            outline
            disabled={loading}
          />
        </S.Buttons>

        <S.Section>
          <span>Mensagens - {conversation?.target_user?.full_name}</span>
          <hr />
        </S.Section>

        <S.Messages ref={conversationRef}>
          {!messages.length ? <span>Nenhuma mensagem encontrada.</span> : null}

          {messages.map((message) => (
            <S.Message
              key={message.id}
              isOwn={parsedJWT?.user?.id === message.user.id}
              active={["Enviado", "Pendente de aprovação", "Aprovado"].includes(
                message.status,
              )}
            >
              <S.MessageInfos>
                <h2>{message.user.full_name}</h2>
                <p>{message.text}</p>

                {message?.file_src ? (
                  <S.MessageFile
                    href={message.file_src}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {message.file_name}
                  </S.MessageFile>
                ) : null}
              </S.MessageInfos>

              {/* O status CRU continua governando a cor; o que muda é o texto
                  — "Pendente" vira "Não visualizado" para quem escreveu. */}
              <S.MessageStatus status={message.status}>
                {messageStatusLabel(message.status)}
              </S.MessageStatus>

              {/* Workflow D23 (29/08/2026): o PATROCINADOR também avalia o
                  arquivo recebido — Aprovado migra para Materiais Aprovados. */}
              {parsedJWT?.user?.is_sponsor &&
              message.user.id !== parsedJWT?.user?.id &&
              message?.file_src &&
              message.status === "Pendente de aprovação" ? (
                <S.Buttons>
                  <Button
                    type="button"
                    outline
                    text="Reprovado"
                    onClick={() => evaluateMessage(message.id, false)}
                    disabled={loading}
                  />
                  <Button
                    type="button"
                    text="Aprovado"
                    onClick={() => evaluateMessage(message.id, true)}
                    disabled={loading}
                  />
                </S.Buttons>
              ) : null}
            </S.Message>
          ))}
        </S.Messages>

        <Input
          type="textarea"
          name="text"
          placeholder="Digite sua mensagem"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={loading}
          height="100px"
        />

        <S.Buttons>
          {file ? (
            <S.File>
              <span>{file.name}</span>
              <GetIcon
                name="X"
                onClick={() => setFile(null)}
                color={theme.colors.neutral.white}
              />
            </S.File>
          ) : (
            <Button
              type="button"
              text="Adicionar Anexo"
              onClick={() => inputRef.current.click()}
              icon="Upload"
              disabled={loading}
              outline
            />
          )}
          <Button
            type="button"
            text={sending ? "Enviando..." : "Enviar"}
            onClick={() => sendMessage(conversation.id)}
            disabled={loading || sending}
          />
        </S.Buttons>

        <S.HiddenInput
          ref={inputRef}
          type="file"
          name="file"
          onChange={handleFileUpload}
        />
      </Form>
    </S.Wrapper>
  );
};

export default ConversationMessages;
